import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaBars,
  FaChevronDown,
  FaCog,
  FaEnvelope,
  FaNewspaper,
  FaSearch,
  FaShieldAlt,
  FaSignOutAlt,
  FaTimes,
  FaUser,
  FaUserCircle,
} from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { useProjectRequest } from '../../contexts/ProjectRequestContext';
import VihsMark from '../VihsMark';
import './NavbarHome.css';

const NAV_LINKS = [
  { href: '#servicos', label: 'Serviços' },
  { href: '#ecossistema', label: 'Ecossistema' },
  { href: '#sobre', label: 'Sobre' },
];

export default function NavbarHome() {
  const { isAuthenticated, user, logout } = useAuth();
  const { openProjectRequest } = useProjectRequest();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef(null);

  const displayName = user?.profile?.displayName || user?.username || 'Minha conta';
  const initials = displayName
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 24);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (!accountMenuOpen) return undefined;

    const closeOnOutsideClick = (event) => {
      if (!accountMenuRef.current?.contains(event.target)) {
        setAccountMenuOpen(false);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setAccountMenuOpen(false);
    };

    document.addEventListener('pointerdown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [accountMenuOpen]);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const handleLogout = async () => {
    setAccountMenuOpen(false);
    setMobileMenuOpen(false);
    await logout();
  };

  return (
    <header className={`home-navbar ${scrolled ? 'scrolled' : ''}`}>
      <div className="home-navbar-container">
        <Link to="/" className="home-logo" onClick={closeMobileMenu}>
          <VihsMark />
          <span className="home-logo-text">Vih&apos;s</span>
        </Link>

        <nav className="home-desktop-nav" aria-label="Principal">
          <ul>
            {NAV_LINKS.map((link) => (
              <li key={link.href}>
                <a href={link.href}>{link.label}</a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="home-nav-actions">
          {isAuthenticated ? (
            <div className="home-account-menu" ref={accountMenuRef}>
              <button
                type="button"
                className={`home-account-trigger${accountMenuOpen ? ' is-open' : ''}`}
                onClick={() => setAccountMenuOpen((open) => !open)}
                aria-expanded={accountMenuOpen}
                aria-haspopup="menu"
              >
                <span className="home-account-avatar" aria-hidden="true">
                  {user?.avatarUrl ? <img src={user.avatarUrl} alt="" /> : initials}
                </span>
                <span className="home-account-trigger-copy">
                  <strong>{displayName}</strong>
                  <small>Minha conta</small>
                </span>
                <FaChevronDown className="home-account-chevron" aria-hidden="true" />
              </button>

              <div className={`home-account-dropdown${accountMenuOpen ? ' is-open' : ''}`} role="menu">
                <div className="home-account-summary">
                  <span className="home-account-avatar home-account-avatar-large" aria-hidden="true">
                    {user?.avatarUrl ? <img src={user.avatarUrl} alt="" /> : initials}
                  </span>
                  <div>
                    <strong>{displayName}</strong>
                    <span><FaEnvelope aria-hidden="true" /> {user?.email}</span>
                  </div>
                </div>

                <div className="home-account-status">
                  <FaShieldAlt aria-hidden="true" />
                  <span>{user?.emailVerified ? 'Conta verificada' : 'Verificação pendente'}</span>
                </div>

                <div className="home-account-dropdown-links">
                  <Link to="/conta" role="menuitem" onClick={() => setAccountMenuOpen(false)}>
                    <FaUser aria-hidden="true" />
                    <span>
                      <strong>Minha conta</strong>
                      <small>Perfil e informações pessoais</small>
                    </span>
                  </Link>
                  <Link to={`/perfil/${user?.username || ''}`} role="menuitem" onClick={() => setAccountMenuOpen(false)}>
                    <FaUserCircle aria-hidden="true" />
                    <span>
                      <strong>Perfil público</strong>
                      <small>Como a comunidade te vê</small>
                    </span>
                  </Link>
                  <Link to="/feed" role="menuitem" onClick={() => setAccountMenuOpen(false)}>
                    <FaNewspaper aria-hidden="true" />
                    <span>
                      <strong>Feed</strong>
                      <small>Publicações de quem você segue</small>
                    </span>
                  </Link>
                  <Link to="/explorar" role="menuitem" onClick={() => setAccountMenuOpen(false)}>
                    <FaSearch aria-hidden="true" />
                    <span>
                      <strong>Explorar</strong>
                      <small>Buscar usuários da Vih&apos;s</small>
                    </span>
                  </Link>
                  <Link to="/conta#account-seguranca" role="menuitem" onClick={() => setAccountMenuOpen(false)}>
                    <FaCog aria-hidden="true" />
                    <span>
                      <strong>Segurança</strong>
                      <small>Senha, conexões e sessões</small>
                    </span>
                  </Link>
                </div>

                <button type="button" className="home-account-signout" role="menuitem" onClick={handleLogout}>
                  <FaSignOutAlt aria-hidden="true" />
                  <span>Sair da conta</span>
                </button>
              </div>
            </div>
          ) : (
            <Link to="/entrar" className="home-nav-login">
              Entrar
            </Link>
          )}
          <button
            type="button"
            className="home-nav-cta"
            onClick={() => {
              setAccountMenuOpen(false);
              openProjectRequest();
            }}
          >
            Solicitar projeto
          </button>
        </div>

        <button
          type="button"
          className="home-mobile-menu-button"
          onClick={() => setMobileMenuOpen((open) => !open)}
          aria-label={mobileMenuOpen ? 'Fechar menu' : 'Abrir menu'}
          aria-expanded={mobileMenuOpen}
        >
          {mobileMenuOpen ? <FaTimes /> : <FaBars />}
        </button>
      </div>

      <div className={`home-mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
        <nav aria-label="Menu móvel">
          <ul>
            {NAV_LINKS.map((link) => (
              <li key={link.href}>
                <a href={link.href} onClick={closeMobileMenu}>
                  {link.label}
                </a>
              </li>
            ))}
            <li>
              {isAuthenticated ? (
                <div className="home-mobile-account">
                  <div className="home-mobile-account-profile">
                    <span className="home-account-avatar" aria-hidden="true">
                      {user?.avatarUrl ? <img src={user.avatarUrl} alt="" /> : initials}
                    </span>
                    <span>
                      <strong>{displayName}</strong>
                      <small>{user?.email}</small>
                    </span>
                  </div>
                  <div className="home-mobile-account-actions">
                    <Link to="/conta" className="home-mobile-login" onClick={closeMobileMenu}>
                      Minha conta
                    </Link>
                    <Link to={`/perfil/${user?.username || ''}`} className="home-mobile-login" onClick={closeMobileMenu}>
                      Perfil público
                    </Link>
                    <Link to="/feed" className="home-mobile-login" onClick={closeMobileMenu}>
                      Feed
                    </Link>
                    <Link to="/explorar" className="home-mobile-login" onClick={closeMobileMenu}>
                      Explorar
                    </Link>
                    <button type="button" onClick={handleLogout}>
                      <FaSignOutAlt /> Sair
                    </button>
                  </div>
                </div>
              ) : (
                <Link to="/entrar" className="home-mobile-login" onClick={closeMobileMenu}>
                  Entrar ou criar conta
                </Link>
              )}
            </li>
            <li>
              <button
                type="button"
                className="home-mobile-cta"
                onClick={() => {
                  closeMobileMenu();
                  openProjectRequest();
                }}
              >
                Solicitar projeto
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </header>
  );
}
